/**
 * Let's add jsdoc comments to every method, interface, class and member.
 * Add lots of comments where ever else we think they will help future
 * developers understand some logic that's not entirely obvious.
 *
 * Let's make sure that no function is over one page long. Break up large
 * functions into descrete  and explainable chunks
 */

import * as React from 'react';
import {
    IDetailsListProps,
    IGroup,
    IGroupDividerProps,
    IDetailsRowProps,
    IDetailsFooterProps
} from 'office-ui-fabric-react/lib/DetailsList';
import {
    IRenderFunction
} from 'office-ui-fabric-react/lib/Utilities';
import { ILayoutRenderProps } from '../../decorators/withLayoutRenderComplete';
import strings from '../../cache/DetailsList.resx';
import { connectToItemCache } from '../../decorators/connectToItemCache';
import { IItemCache } from '../../../models/IItemCacheObserver';
import { ItemPage } from '../../itemPage/ItemPage';
import { IItemDetailsRowProps, ItemDetailsRow } from '../ItemDetailsRow';
import { IDetailsListItem } from '../IDetailsListItem';
import { itemSetDetailsListControl } from '../../../controls/itemSetDetailsListControl';
import { IDetailsGroupItem } from '../IDetailsGroupItem';
import { GroupItemDetailsHeader } from '../GroupItemDetailsHeader';
import { childItemsControl } from '../../../controls/childItemsControl';
import { Selection } from 'office-ui-fabric-react/lib/Selection';
import { childItemsFacet, pageFacet } from '../../../facets/SetFacets';
import { isGroupFacet, countFacet, defaultCollapsedFacet, GROUP_ITEM_LIMIT } from '../../../facets/GroupFacets';
import { displayNameFacet } from '../../../facets/ItemFacets';
import { errorFacet } from '../../../facets/DemandFacets';
import { IComponentDecorator, IDecoratedProps } from '../../../utilities/components/Decorators';
import { allGroupsCollapsedFacet } from '../../../facets/ScopeFacets';

const DEFAULT_RENDERED_WINDOWS_BEHIND = 0;
const DEFAULT_RENDERED_WINDOWS_AHEAD = 0;

export interface IOverrideDetailsListProps {
    detailsListOverrides: Partial<IDetailsListProps>;
}

/**
 * Props added to a component by the item set row-and-group mapping decorator.
 */
export interface IWithItemSetRowsAndGroupsProps {
    /**
     * The key for an item set. This is 'itemKey' for compatibility with other decorators.
     */
    itemKey: string | undefined;
    /**
     * A mapping of group items to their expanded or collapsed state.
     * This might be provided by an outer decorator.
     */
    groupsCollapsed?: { [key: string]: boolean } | undefined;
    /**
     * Whether or not all groups should be force-collapsed.
     */
    allGroupsCollapsed?: boolean;
    /**
     * Callback when a group should have is collapsed state toggled.
    */
    onToggleGroupCollapsed?: (payload: {
        /**
         * The key for the group to toggle.
         */
        key: string;
        /**
         * Whether or not the group should now be collapsed.
         */
        isCollapsed: boolean;
    }) => void;
    /**
     * Callback when all groups should be expanded or collapsed.
     */
    onToggleCollapseAll?: (payload: {
        groups: IGroup[];
        newAllCollapsed: boolean;
    }) => void;
    /**
     * Specifies whether groups should have their children limited, and show a button
     * to view the rest.
     */
    onGetGroupItemLimit?: (payload: {
        key: string;
        count: number | undefined;
        isShowingAll: boolean | undefined;
    }) => number;
    /**
     * Selection model for the details list
     */
    selection?: Selection;
    /**
     * Callback when an item is 'invoked' via SelectionZone.
     * This will be reused to invoke groups so they are handled just like other items.
     */
    onItemInvoked?: IDetailsListProps['onItemInvoked'];
    /**
     * if shimmer should be rendered, host app is able to set it.
     */
    enableShimmer?: boolean;
    /**
     * Callback for details list footer
     */
    onRenderDetailsFooter?: IRenderFunction<IDetailsFooterProps>;
}

/**
 * Props injected by the item set row-and-groups mapping into DetailsList,
 * so they do not need to be specified by the parent component.
 */
export type IWithItemSetRowsAndGroupsInjectedProps =
    Pick<IDetailsListProps, 'items' | 'groups' | 'setKey' | 'onRenderRow' | 'groupProps' |
    'enableShimmer' | 'onRenderDetailsFooter' | 'listProps'> &
    IOverrideDetailsListProps &
    Pick<ILayoutRenderProps, 'isDataReadyForLayout'>;

export type IDecorateWithItemSetRowsAndGroupsProps<TProps extends IWithItemSetRowsAndGroupsInjectedProps> =
    IDecoratedProps<TProps, IWithItemSetRowsAndGroupsInjectedProps, IWithItemSetRowsAndGroupsProps>;

/**
 * Decorator, typically applied to a DetailsList, which maps the child items of a given item set (by key)
 * to the rows and groups props of DetailsList.
 *
 * Use this decorator to provide basic data-binding to DetailsList given an item set.
 */
export function withItemSetRowsAndGroups(): IComponentDecorator<IWithItemSetRowsAndGroupsInjectedProps, IWithItemSetRowsAndGroupsProps> {
    return connectToItemCache(mapItemSetToRowsAndGroups);
}

/**
 * Memoization for an empty array of items.
 * This can help cut down on render churn if multiple decorator mapping
 * passes produce empty item lists.
 */
const NO_ITEMS: IDetailsListItem[] = [];

/**
 * Function which iterates child items, pages, and nested groups within a top-level
 * item set, and produces a flattened list of rows and groups for use when rendering a
 * DetailsList.
 * @param itemCache - An item cache accessor mapped to the current state of the cache.
 * @param props - Props to control the behavior of the mapping.
 */
function mapItemSetToRowsAndGroups(
    itemCache: IItemCache,
    props: IWithItemSetRowsAndGroupsProps
): IWithItemSetRowsAndGroupsInjectedProps {
    // TODO This needs to be memoized.
    const {
        groupsCollapsed,
        allGroupsCollapsed = itemCache.demandItemFacet(allGroupsCollapsedFacet, props.itemKey),
        onToggleGroupCollapsed,
        onToggleCollapseAll,
        onGetGroupItemLimit,
        enableShimmer,
        selection
    } = props;

    const items: (IDetailsListItem | undefined)[] = [];

    let index = 0;

    const groups: (IGroup & { data: IDetailsGroupItem; })[] = [];

    const childItems = itemCache.demandItemFacet(childItemsFacet, props.itemKey);

    const childItemsControlHandler = itemCache.demandItemFacet(childItemsControl, props.itemKey);

    const {
        itemKeys
    } = childItemsControl.evaluate(childItemsControlHandler)(itemCache, {
        itemKey: props.itemKey,
        itemKeys: childItems && childItems.itemKeys
    });

    const controlHandler = itemCache.demandItemFacet(itemSetDetailsListControl, props.itemKey);

    const {
        rowRenderer: ItemRowRenderer,
        groupHeaderRenderer: GroupHeaderRenderer,
        groupProps,
        listProps,
        itemSetKey,
        onRenderDetailsFooter,
        ...detailsListOverrides
    } = itemSetDetailsListControl.evaluate(controlHandler)(itemCache, {
        itemSetKey: props.itemKey,
        rowRenderer: ItemDetailsRow,
        groupProps: {},
        listProps: {
            renderedWindowsAhead: DEFAULT_RENDERED_WINDOWS_AHEAD,
            renderedWindowsBehind: DEFAULT_RENDERED_WINDOWS_BEHIND
        },
        groupHeaderRenderer: GroupItemDetailsHeader,
        onRenderDetailsFooter: props.onRenderDetailsFooter
    });

    if (itemSetKey && itemKeys) {
        for (const childItemKey of itemKeys) {
            if (itemCache.isItemFacetResolved(pageFacet, childItemKey)) {
                // Item is a placeholder. Do nothing, since only rendering
                // a row should force resolution.
                items.push({
                    key: childItemKey,
                    itemKey: childItemKey,
                    itemSetKey: itemSetKey,
                    Renderer: ItemDetailsPage
                });

                if (groups.length > 0) {
                    groups.push({
                        key: 'groupPage-' + childItemKey,
                        name: '',
                        startIndex: index,
                        count: 1,
                        isCollapsed: false,
                        data: {
                            itemKey: childItemKey,
                            itemSetKey: itemSetKey,
                            selection: selection,
                            HeaderRenderer: GroupItemDetailsPage
                        }
                    });
                }
            } else if (itemCache.isItemFacetResolved(isGroupFacet, childItemKey)) {
                const {
                    displayName,
                    groupCount,
                    defaultCollapsed
                } = itemCache.demandItem({
                    displayName: displayNameFacet,
                    groupCount: countFacet,
                    defaultCollapsed: defaultCollapsedFacet
                }, childItemKey);

                const startIndex = index;
                const nestedGroups: typeof groups = [];

                let groupIsShowingAll = true;
                let groupChildItems: (typeof childItemsFacet.type) | undefined;

                if (itemCache.isItemFacetResolved(childItemsFacet, childItemKey) &&
                    (groupChildItems = itemCache.demandItemFacet(childItemsFacet, childItemKey))) {
                    // If the childItemsFacet is resolved, the group either contains nested groups or is expanded.
                    if (itemCache.isItemFacetResolved(isGroupFacet, groupChildItems.itemKeys[0])) {
                        // Nested group scenario
                        let nestedGroupChildItems: (typeof childItemsFacet.type) | undefined;
                        let nestedStartIndex = index;

                        let nestedIsShowingAll = true;
                        for (const nestedGroupKey of groupChildItems.itemKeys) {
                            const {
                                nestedGroupDisplayName,
                                nestedGroupCount,
                                nestedGroupDefaultCollapsed
                            } = itemCache.demandItem({
                                nestedGroupDisplayName: displayNameFacet,
                                nestedGroupCount: countFacet,
                                nestedGroupDefaultCollapsed: defaultCollapsedFacet
                            }, nestedGroupKey);

                            if (itemCache.isItemFacetResolved(childItemsFacet, nestedGroupKey) &&
                                (nestedGroupChildItems = itemCache.demandItemFacet(childItemsFacet, nestedGroupKey))) {
                                // If the childItemsFacet of the nested group is resolved, the nested group is expanded.
                                let nestedGroupItemKeys = nestedGroupChildItems.itemKeys;
                                let count = nestedGroupCount || (index - nestedStartIndex);
                                if (nestedGroupItemKeys.length > GROUP_ITEM_LIMIT) {
                                    nestedIsShowingAll = false;
                                    nestedGroupItemKeys = nestedGroupItemKeys.slice(0, GROUP_ITEM_LIMIT);
                                    count = GROUP_ITEM_LIMIT;
                                }
                                for (const groupItemKey of nestedGroupItemKeys) {
                                    let onRender = itemCache.isItemFacetResolved(pageFacet, groupItemKey) ?
                                        ItemDetailsPage :
                                        ItemRowRenderer;
                                    items.push({
                                        key: groupItemKey,
                                        itemKey: groupItemKey,
                                        itemSetKey: itemSetKey,
                                        Renderer: onRender
                                    });
                                    index++;
                                }
                                if (nestedGroupItemKeys.length < count) {
                                    for (let i = nestedGroupItemKeys.length; i < count; i++) {
                                        items.push(undefined);
                                        index++;
                                    }
                                }
                            } else {
                                // Nested group collapsed
                                items.push({
                                    key: nestedGroupKey,
                                    itemKey: nestedGroupKey,
                                    itemSetKey: itemSetKey,
                                    Renderer: ItemDetailsPage
                                });

                                index++;
                                if (nestedGroupCount) {
                                    for (let i = 1; i < nestedGroupCount; i++) {
                                        items.push(undefined);
                                        index++;
                                    }
                                }
                            }

                            const nestedGroupCollapsed = groupsCollapsed && groupsCollapsed[nestedGroupKey] !== undefined ?
                                groupsCollapsed[nestedGroupKey] :
                                (allGroupsCollapsed !== undefined ? allGroupsCollapsed : nestedGroupDefaultCollapsed);

                            if (itemCache.isItemFacetResolved(pageFacet, nestedGroupKey)) {
                                nestedGroups.push({
                                    key: 'groupPage-' + nestedGroupKey,
                                    name: '',
                                    startIndex: nestedStartIndex,
                                    count: 1,
                                    level: 1,
                                    isCollapsed: false,
                                    data: {
                                        itemKey: nestedGroupKey,
                                        itemSetKey: itemSetKey,
                                        selection: selection,
                                        HeaderRenderer: GroupItemDetailsPage
                                    }
                                });
                            } else {
                                nestedGroups.push({
                                    key: nestedGroupKey,
                                    startIndex: nestedStartIndex,
                                    count: nestedGroupCount || (index - nestedStartIndex),
                                    level: 1,
                                    isCollapsed: nestedGroupCollapsed,
                                    isShowingAll: nestedIsShowingAll,
                                    name: nestedGroupDisplayName || '',
                                    data: {
                                        itemKey: nestedGroupKey,
                                        itemSetKey: itemSetKey,
                                        selection: selection,
                                        HeaderRenderer: GroupHeaderRenderer
                                    }
                                });
                            }

                            nestedStartIndex = index;
                        }
                    } else {
                        // Group expanded
                        let groupItemKeys = groupChildItems.itemKeys;
                        let count = groupCount || (index - startIndex);
                        if (groupItemKeys.length > GROUP_ITEM_LIMIT) {
                            groupIsShowingAll = false;
                            groupItemKeys = groupItemKeys.slice(0, GROUP_ITEM_LIMIT);
                            count = GROUP_ITEM_LIMIT;
                        }
                        for (const groupItemKey of groupItemKeys) {
                            let onRender = itemCache.isItemFacetResolved(pageFacet, groupItemKey) ?
                                ItemDetailsPage :
                                ItemRowRenderer;

                            items.push({
                                key: groupItemKey,
                                itemKey: groupItemKey,
                                itemSetKey: itemSetKey,
                                Renderer: onRender
                            });
                            index++;
                        }
                        if (groupItemKeys.length < count) {
                            for (let i = groupItemKeys.length; i < count; i++) {
                                items.push(undefined);
                                index++;
                            }
                        }
                    }
                } else {
                    // If the childItemsFacet is not resolved, the group is collapsed.
                    items.push({
                        key: childItemKey,
                        itemKey: childItemKey,
                        itemSetKey: itemSetKey,
                        Renderer: ItemDetailsPage
                    });

                    index++;
                    if (groupCount) {
                        for (let i = 1; i < groupCount; i++) {
                            items.push(undefined);
                            index++;
                        }
                    }
                }

                const groupCollapsed = groupsCollapsed && groupsCollapsed[childItemKey] !== undefined ?
                    groupsCollapsed[childItemKey] : (allGroupsCollapsed !== undefined ? allGroupsCollapsed : defaultCollapsed);

                groups.push({
                    key: childItemKey,
                    startIndex: startIndex,
                    count: groupCount || (index - startIndex),
                    isCollapsed: groupCollapsed,
                    isShowingAll: groupIsShowingAll,
                    name: displayName || '',
                    children: nestedGroups.length > 0 ? nestedGroups : undefined,
                    data: {
                        itemKey: childItemKey,
                        itemSetKey: itemSetKey,
                        selection: selection,
                        HeaderRenderer: GroupHeaderRenderer
                    }
                });
            } else {
                items.push({
                    key: childItemKey,
                    itemKey: childItemKey,
                    itemSetKey: itemSetKey,
                    Renderer: ItemRowRenderer
                });
                index++;
            }
        }
    }

    const toggleGroupCollapsed = onToggleGroupCollapsed ? (group: IGroup) => {
        onToggleGroupCollapsed({
            key: group.key,
            isCollapsed: !!group.isCollapsed
        });
    } : undefined;

    const toggleCollapseAll = onToggleCollapseAll ? (newAllCollapsed: boolean) => {
        onToggleCollapseAll({
            groups: groups,
            newAllCollapsed: newAllCollapsed
        });
    } : undefined;

    const getGroupItemLimit = onGetGroupItemLimit ? (group: IGroup) => {
        return onGetGroupItemLimit({
            key: group.key,
            count: group.count,
            isShowingAll: group.isShowingAll
        });
    } : undefined;

    const isDataReadyForLayout = itemCache.isItemFacetResolved(childItemsFacet, props.itemKey) ||
        itemCache.isItemFacetResolved(errorFacet, props.itemKey);

    const finalEnableShimmer = typeof enableShimmer === 'undefined' ?
        !isDataReadyForLayout : enableShimmer;

    return {
        detailsListOverrides,
        onRenderDetailsFooter,
        items: items || NO_ITEMS,
        groups: groups.length > 0 ? groups : undefined,
        groupProps: {
            ...groupProps,
            getGroupItemLimit: getGroupItemLimit,
            onToggleCollapseAll: toggleCollapseAll,
            headerProps: {
                onToggleCollapse: toggleGroupCollapsed,
                onGroupHeaderClick: props.onItemInvoked,
                isCollapsedGroupSelectVisible: false
            },
            showAllProps: {
                onToggleSummarize: props.onItemInvoked,
                showAllLinkText: strings.ShowAll
            },
            onRenderHeader: renderGroupHeader,
            isAllGroupsCollapsed: allGroupsCollapsed
        },
        listProps: listProps,
        setKey: props.itemKey,
        onRenderRow: onRenderRow,
        isDataReadyForLayout: isDataReadyForLayout,
        enableShimmer: finalEnableShimmer
    };
}

/**
 * Render function to be passed as onRenderHeader to the group props for DetailsList.
 */
function renderGroupHeader(props?: IGroupDividerProps, defaultRender?: (props?: IGroupDividerProps) => JSX.Element | null):
    JSX.Element | null {
    if (!props || !props.group) {
        return null;
    }

    const {
        HeaderRenderer,
        ...headerProps
    } = props.group.data as IDetailsGroupItem;

    return (
        <HeaderRenderer
            { ...props }
            { ...headerProps }
            defaultRender={ defaultRender }
        />
    );
}

/**
 * Wrapper for ItemPage which fixes the props for use as an item row renderer.
 */
function ItemDetailsPage(props: IItemDetailsRowProps): JSX.Element { // tslint:disable-line:function-name
    return (
        <ItemPage itemKey={ props.itemKey } showEmptyItem={ true } />
    );
}

function GroupItemDetailsPage(props: {}): JSX.Element | null { // tslint:disable-line:function-name
    return null;
}

/**
 * Render function to be passed to DetailsList onRenderRow
 * which delegates render behavior to the item itself.
 *
 * This implements the IRenderFunction contract expected by IDetailsListProps.
 */
function onRenderRow(
    props?: IDetailsRowProps | undefined,
    defaultRender?: IRenderFunction<IDetailsRowProps> | undefined
): JSX.Element | null {
    if (!props) {
        return null;
    }

    const {
        Renderer,
        ...rowProps
    }: IDetailsListItem = props.item;

    return (
        <Renderer
            { ...props }
            { ...rowProps }
            defaultRender={ defaultRender }
        />
    );
}
